import React from "react";
import mainLogo from "../assets/images/mainLogo.png";
import { isIOS, isAndroid } from "../utils/platform";

const ErrorScreen = ({ errorMessage, onReload, onContact, langString = "en", showUpdateButton = false, onUpdate }) => {
  const handleContactClick = async () => {
    const emailAddress = "info@smartfriendsapp.com";
    const subject = "WordHit Question";
    const mailtoLink = `mailto:${emailAddress}?subject=${encodeURIComponent(subject)}`;
    
    // Try to open mail composer
    try {
      // On iOS/Capacitor, use native mail composer
      if (typeof window !== "undefined" && window.Capacitor) {
        const platform = window.Capacitor.getPlatform();
        if (platform === "ios") {
          try {
            // Try to use native mail composer plugin via registerPlugin
            const { default: MailComposer } = await import('../utils/mailComposer');
            
            if (MailComposer) {
              console.log("MailComposer plugin found via registerPlugin, attempting to open...");
              await MailComposer.composeMail({
                recipients: [emailAddress],
                subject: subject,
                body: ""
              });
              console.log("MailComposer opened successfully");
              return;
            }
          } catch (pluginError) {
            console.error("Native mail composer error:", pluginError);
            // Fall through to mailto: link
          }
          
          // Fallback to mailto: link if plugin not available
          console.log("Falling back to mailto: link");
          window.location.href = mailtoLink;
          return;
        }
      }
      
      // For web, try window.open first
      const mailWindow = window.open(mailtoLink, "_blank");
      if (!mailWindow) {
        // Fallback: Create a hidden anchor and click it
        const anchor = document.createElement("a");
        anchor.href = mailtoLink;
        anchor.style.display = "none";
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      }
    } catch (error) {
      console.error("Failed to open email client:", error);
    }
  };

  const handleUpdateClick = async () => {
    if (onUpdate) {
      onUpdate();
    } else {
      // Determine store URL based on platform
      // Use direct window.Capacitor check first (most reliable), then fallback to utility functions
      let storeUrl;
      const platform = typeof window !== "undefined" && window.Capacitor 
        ? window.Capacitor.getPlatform() 
        : null;
      
      // Additional Android detection via user agent as fallback
      const userAgent = typeof navigator !== "undefined" ? navigator.userAgent.toLowerCase() : '';
      const isAndroidUserAgent = userAgent.includes('android');
      
      console.log('[ErrorScreen] Platform detection:', { 
        platform, 
        isAndroid: isAndroid(), 
        isIOS: isIOS(),
        isAndroidUserAgent,
        hasCapacitor: typeof window !== "undefined" && !!window.Capacitor
      });
      
      if (platform === 'android' || (!platform && (isAndroid() || isAndroidUserAgent))) {
        // Android: Google Play Store
        const packageName = 'com.wordhit.app';
        storeUrl = `https://play.google.com/store/apps/details?id=${packageName}`;
        console.log('[ErrorScreen] Using Google Play Store URL');
      } else if (platform === 'ios' || (!platform && isIOS())) {
        // iOS: App Store
        const appStoreId = '6755532039';
        storeUrl = `https://apps.apple.com/app/id${appStoreId}`;
        console.log('[ErrorScreen] Using App Store URL');
      } else {
        // Web/fallback: default to App Store
      const appStoreId = '6755532039';
        storeUrl = `https://apps.apple.com/app/id${appStoreId}`;
        console.log('[ErrorScreen] Using App Store URL (fallback)');
      }
      
      // Use Capacitor's Browser plugin if available (most reliable for native apps)
      if (typeof window !== "undefined" && window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.Browser) {
        try {
          await window.Capacitor.Plugins.Browser.open({ url: storeUrl });
          return;
        } catch (error) {
          console.error("Browser.open failed:", error);
        }
      }
      
      // Fallback: Create a hidden anchor and click it
      try {
        const link = document.createElement('a');
        link.href = storeUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Failed to open store link:", error);
      }
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: '#fefbef',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999,
      padding: '20px',
      boxSizing: 'border-box'
    }}>
      {/* Logo */}
      <img 
        src={mainLogo} 
        alt="WordHit Logo"
        style={{
          width: '220px',
          height: 'auto',
          maxWidth: '80vw',
          marginBottom: '40px'
        }}
      />

      {/* Error message */}
      <div style={{
        fontFamily: 'Union, sans-serif',
        fontSize: '1.2rem',
        color: '#E62731',
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: '30px',
        maxWidth: '600px'
      }}>
        {errorMessage || (langString === "nl" 
          ? "De game kon niet geladen worden." 
          : "Unable to load the game.")}
      </div>

      {/* Reload or Update button */}
      {showUpdateButton ? (
        <button
          onClick={handleUpdateClick}
          style={{
            fontFamily: 'Union, sans-serif',
            fontSize: '1.1rem',
            fontWeight: 'bold',
            color: 'white',
            backgroundColor: '#194093',
            border: 'none',
            borderRadius: '8px',
            padding: '14px 32px',
            cursor: 'pointer',
            marginBottom: '20px',
            minWidth: '200px',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#153075'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#194093'}
        >
          {langString === "nl" ? "Update app" : "Update app"}
        </button>
      ) : (
        <button
          onClick={onReload}
          style={{
            fontFamily: 'Union, sans-serif',
            fontSize: '1.1rem',
            fontWeight: 'bold',
            color: 'white',
            backgroundColor: '#194093',
            border: 'none',
            borderRadius: '8px',
            padding: '14px 32px',
            cursor: 'pointer',
            marginBottom: '20px',
            minWidth: '200px',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#153075'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#194093'}
        >
          {langString === "nl" ? "Opnieuw laden" : "Reload"}
        </button>
      )}

      {/* Contact section */}
      <div style={{
        fontFamily: 'Union, sans-serif',
        fontSize: '0.95rem',
        color: 'black',
        textAlign: 'center',
        marginTop: '20px',
        maxWidth: '500px',
        lineHeight: '1.6'
      }}>
        {langString === "nl" 
          ? "Denk je dat er iets mis gaat, neem contact op" 
          : "Think something is going wrong? Contact us"}
      </div>

      {/* Contact button */}
      <button
        onClick={handleContactClick}
        style={{
          fontFamily: 'Union, sans-serif',
          fontSize: '1rem',
          fontWeight: 'normal',
          color: '#194093',
          backgroundColor: 'transparent',
          border: '2px solid #194093',
          borderRadius: '8px',
          padding: '12px 28px',
          cursor: 'pointer',
          marginTop: '15px',
          minWidth: '180px',
          transition: 'all 0.2s'
        }}
        onMouseOver={(e) => {
          e.target.style.backgroundColor = '#194093';
          e.target.style.color = 'white';
        }}
        onMouseOut={(e) => {
          e.target.style.backgroundColor = 'transparent';
          e.target.style.color = '#194093';
        }}
      >
        {langString === "nl" ? "Contact" : "Contact"}
      </button>
    </div>
  );
};

export default ErrorScreen;

